import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Phone, Hash, ArrowLeft, AlertCircle, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { TennisBookingTutorial } from '../components/TennisBookingTutorial';
import { readJson } from '../lib/fetchHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TennisRegister = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    membership_number: '',
    password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirm_password) {
      setError(t('tennis.passwordMismatch'));
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError(t('tennis.passwordTooShort'));
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/tennis/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          membership_number: formData.membership_number || null,
          password: formData.password
        })
      });

      // Defensive: clone to avoid "body stream already read" if any interceptor consumed it
      const data = await readJson(response);

      if (!response.ok) {
        throw new Error(data.detail || t('common.error'));
      }

      setSuccess(true);
      toast.success(t('tennis.registrationSuccess'));
    } catch (err) {
      setError(err.message || 'Kunne ikke opprette konto. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="tennis-register-success" className="min-h-screen flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">{t('tennis.registrationSuccess')}</h1>
          <p className="text-slate-500 dark:text-slate-400 mb-8">
            {t('tennis.awaitingApproval')}
          </p>
          <Link to="/tennis/login">
            <Button className="bg-green-500 text-white font-bold hover:bg-green-500/90">
              {t('tennis.goToLogin')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="tennis-register-page" className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full">
        {/* Back button */}
        <Link to="/tennis" className="inline-flex items-center text-slate-500 hover:text-slate-900 dark:hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('tennis.backToTennis')}
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🎾</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t('tennis.createAccount')}</h1>
          <p className="text-slate-500 mt-2">{t('tennis.bookingSystem')}</p>
        </div>

        {/* Tutorial */}
        <div className="mb-6">
          <TennisBookingTutorial defaultOpen />
        </div>

        {/* Form wrapper - same max width as before */}
        <div className="max-w-md mx-auto">

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-8">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">{t('tennis.firstName')}</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    data-testid="tennis-register-firstname"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="pl-10 bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
                    placeholder="Ola"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">{t('tennis.lastName')}</label>
                <Input
                  type="text"
                  data-testid="tennis-register-lastname"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
                  placeholder="Nordmann"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">{t('tennis.email')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="email"
                  data-testid="tennis-register-email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
                  placeholder="din@epost.no"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">{t('tennis.phone')}</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="tel"
                  data-testid="tennis-register-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="pl-10 bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
                  placeholder="+47 123 45 678"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">{t('tennis.password')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="password"
                  data-testid="tennis-register-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
                  placeholder={t('tennis.passwordMinLength', 'At least 6 characters')}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">{t('tennis.confirmPassword')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="password"
                  data-testid="tennis-register-confirm"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                  className="pl-10 bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
                  placeholder={t('tennis.repeatPassword', 'Repeat password')}
                  required
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            data-testid="tennis-register-submit"
            className="w-full mt-6 bg-green-500 text-white font-bold hover:bg-green-500/90"
            disabled={loading}
          >
            {loading ? t('tennis.registering') : t('tennis.submitRegister')}
          </Button>

          <p className="text-center text-slate-500 text-sm mt-6">
            {t('tennis.alreadyMember')}{' '}
            <Link to="/tennis/login" className="text-green-500 hover:underline">
              {t('tennis.loginHere')}
            </Link>
          </p>
        </form>

        {/* Info box */}
        <div className="mt-6 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <p className="text-slate-600 dark:text-slate-300 text-sm">
            <strong className="text-yellow-500">{t('tennis.tip')}:</strong> {t('tennis.importantNote')}
          </p>
        </div>
        </div>
      </div>
    </div>
  );
};

export default TennisRegister;
